import { QueryClient, DefaultOptions } from '@tanstack/react-query'

/**
 * TanStack Query configuration for optimal performance and caching.
 * 
 * Implements "stale-while-revalidate" strategy:
 * - Data is served instantly from cache when available
 * - Background refetch keeps data fresh
 * - Reduces perceived latency to near-zero for navigation
 */

const queryConfig: DefaultOptions = {
  queries: {
    // Stale time: Data is considered fresh for 5 minutes
    // During this time, no refetch occurs on mount/window focus
    staleTime: 5 * 60 * 1000, // 5 minutes

    // Cache time: Inactive data is kept in cache for 10 minutes
    // After this, data is garbage collected
    gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)

    // OPTIMIZATION: Disable refetch on window focus to reduce network requests
    // This was causing excessive API calls when switching tabs
    refetchOnWindowFocus: false,

    // Automatically refetch when network reconnects
    refetchOnReconnect: true,

    // Don't refetch on mount if data is still fresh
    refetchOnMount: false,

    // Retry failed requests once before giving up
    retry: 1,

    // Exponential backoff for retries (1s, 2s, 4s...)
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  },
  mutations: {
    // Retry mutations only on network errors, not on 4xx/5xx
    retry: false,
  },
}

/**
 * Create a new QueryClient instance with optimal defaults.
 * 
 * Usage:
 * ```tsx
 * import { QueryClientProvider } from '@tanstack/react-query'
 * import { getQueryClient } from '@/lib/query-client'
 * 
 * export function Providers({ children }) {
 *   const queryClient = getQueryClient()
 *   return (
 *     <QueryClientProvider client={queryClient}>
 *       {children}
 *     </QueryClientProvider>
 *   )
 * }
 * ```
 */
export function getQueryClient() {
  return new QueryClient({
    defaultOptions: queryConfig,
  })
}

/**
 * Singleton query client instance for the app.
 * This ensures the same cache is used across the entire application.
 */
let browserQueryClient: QueryClient | undefined = undefined

export function getBrowserQueryClient() {
  if (typeof window === 'undefined') {
    // Server: always make a new query client
    return getQueryClient()
  } else {
    // Browser: make a new query client if we don't already have one
    if (!browserQueryClient) {
      browserQueryClient = getQueryClient()
    }
    return browserQueryClient
  }
}
